import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import { useAlert } from './CustomAlert/AlertContext';
import api from '../utils/api';
import './RoomSelectionSchedule.css';

const TeamManagementView = ({ onBack, hideNavbar = false }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [projectFilter, setProjectFilter] = useState('ALL');

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { showConfirm, showAlert } = useAlert();

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    user_type: 'HR',
    exit_status: 'ACTIVE',
    joined_date: '',
    project_id: '',
    custom_project_name: '',
    use_custom_project: false
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/auth/users', {
        headers: { 'x-user-id': currentUserId }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showAlert('Failed to fetch team users.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get('/api/projects/list');
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('Failed to fetch projects list:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchProjects();
  }, []);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const hasActiveFilters = searchQuery !== '' || roleFilter !== 'ALL' || statusFilter !== 'ALL' || projectFilter !== 'ALL';

  const handleClearFilters = () => {
    setSearchQuery('');
    setRoleFilter('ALL');
    setStatusFilter('ALL');
    setProjectFilter('ALL');
  };

  const filteredUsers = users.filter(user => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || (
      (user.full_name && user.full_name.toLowerCase().includes(q)) ||
      (user.email && user.email.toLowerCase().includes(q)) ||
      (user.phone_number && user.phone_number.toLowerCase().includes(q)) ||
      (user.project_name && user.project_name.toLowerCase().includes(q))
    );

    const userRole = (user.user_type === 'INTERVIEWER' || user.user_type === 'EMPLOYEE') ? 'EMPLOYEE' : user.user_type;
    const matchesRole = roleFilter === 'ALL' || userRole === roleFilter;

    const userStatus = user.exit_status || 'ACTIVE';
    const matchesStatus = statusFilter === 'ALL' || userStatus === statusFilter;

    const projectName = user.project_name || '';
    let matchesProject = true;
    if (projectFilter === 'UNASSIGNED') {
      matchesProject = !projectName || projectName === 'Not Assigned' || projectName === '-';
    } else if (projectFilter !== 'ALL') {
      matchesProject = projectName.toLowerCase() === projectFilter.toLowerCase();
    }

    return matchesSearch && matchesRole && matchesStatus && matchesProject;
  });

  const openAddModal = () => {
    setModalMode('add');
    setSelectedUser(null);
    const today = new Date().toISOString().split('T')[0];
    setFormData({
      full_name: '',
      email: '',
      phone_number: '',
      user_type: 'HR',
      exit_status: 'ACTIVE',
      joined_date: today,
      project_id: '',
      custom_project_name: '',
      use_custom_project: false
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    const rawDate = user.joining_date || user.created_at;
    const formattedJoinedDate = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';
    const roleVal = user.user_type === 'INTERVIEWER' ? 'HR' : user.user_type;

    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      user_type: roleVal || 'HR',
      exit_status: user.exit_status || 'ACTIVE',
      joined_date: formattedJoinedDate,
      project_id: user.project_id ? String(user.project_id) : '',
      custom_project_name: '',
      use_custom_project: false
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (user.id === currentUserId) {
      showAlert('You cannot remove yourself.', 'error');
      return;
    }
    const confirmed = await showConfirm(
      `Are you sure you want to remove ${user.full_name}? They will lose access to the platform.`
    );
    if (confirmed) {
      setLoading(true);
      try {
        await api.delete(`/api/auth/users/${user.id}`, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User removed successfully', 'success');
        fetchUsers();
      } catch (error) {
        showAlert(error.response?.data?.error || 'Failed to remove user', 'error');
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      user_type: formData.user_type === 'EMPLOYEE' ? 'INTERVIEWER' : formData.user_type
    };

    try {
      if (modalMode === 'add') {
        await api.post('/api/auth/users', payload, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User added successfully.', 'success');
      } else {
        await api.put(`/api/auth/users/${selectedUser.id}`, payload, {
          headers: { 'x-user-id': currentUserId }
        });
        showAlert('User updated successfully', 'success');
      }
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      showAlert(error.response?.data?.error || 'Failed to save user', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const isProjectAlreadyAssigned = Boolean(
    selectedUser &&
    selectedUser.project_name &&
    selectedUser.project_name !== 'Not Assigned' &&
    selectedUser.project_name !== '-'
  );

  return (
    <div className="room-selection-container fade-in">
      {!hideNavbar && (
        <Navbar 
          isLoggedIn 
          userName={currentUser.full_name} 
          onHome={onBack || (() => navigate('/interview'))} 
        />
      )}

      <div className="main-content" style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '16px' }}>
          <button 
            className="schedule-back-btn" 
            onClick={onBack || (() => navigate('/interview'))}
          >
            <span aria-hidden="true">←</span> Back to Dashboard
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '4px' }}>
              Team Management
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14.5px', margin: 0 }}>
              Manage users, roles, project allocations, and employee statuses for your organization.
            </p>
          </div>
          <button 
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              borderRadius: '999px',
              border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: '#ffffff',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span> Add User
          </button>
        </div>

        <div style={{
          background: 'var(--bg-card)',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--border-card)'
        }}>
          {/* Advanced Search & Filters Bar */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ position: 'relative', flex: '1 1 300px', maxWidth: '400px' }}>
              <input 
                type="text" 
                placeholder="Search by name, email, phone, or project..." 
                value={searchQuery}
                onChange={handleSearch}
                style={{
                  width: '100%',
                  padding: '10px 16px 10px 36px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              />
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '15px' }}>
                🔍
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: '13.5px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Roles</option>
                <option value="HR">HR Admin</option>
                <option value="MANAGER">Project Manager</option>
                <option value="BU_HEAD">Business Unit Head</option>
                <option value="EMPLOYEE">Employee</option>
              </select>

              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: '13.5px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="EXIT_PENDING">Exit Pending</option>
                <option value="EXIT_APPROVED">Exit Approved</option>
              </select>

              <select
                value={projectFilter}
                onChange={e => setProjectFilter(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: '13.5px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">All Projects</option>
                <option value="UNASSIGNED">Unassigned</option>
                {projects.map(p => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  style={{
                    padding: '9px 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#f8fafc',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>✕</span> Clear
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '16px' }}>
              <div className="schedule-docx-spinner"></div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '15px', fontWeight: '500' }}>
                Loading team users...
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Name</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Email</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Role</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Project</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Status</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap' }}>Joined</th>
                    <th style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontWeight: '600', fontSize: '13px', textAlign: 'right', whiteSpace: 'nowrap' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        No team users found matching filters.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => {
                      const displayRole = (user.user_type === 'INTERVIEWER' || user.user_type === 'EMPLOYEE') ? 'EMPLOYEE' : user.user_type;
                      return (
                        <tr key={user.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.2s' }}>
                          <td style={{ padding: '14px', fontWeight: '600', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{user.full_name}</td>
                          <td style={{ padding: '14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{user.email}</td>
                          <td style={{ padding: '14px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '700',
                              background: displayRole === 'HR' ? 'rgba(79, 70, 229, 0.1)' : 
                                          displayRole === 'MANAGER' ? 'rgba(249, 115, 22, 0.1)' :
                                          displayRole === 'BU_HEAD' ? 'rgba(147, 51, 234, 0.1)' :
                                          'rgba(13, 148, 136, 0.1)',
                              color: displayRole === 'HR' ? '#4f46e5' : 
                                     displayRole === 'MANAGER' ? '#ea580c' :
                                     displayRole === 'BU_HEAD' ? '#9333ea' :
                                     '#0d9488'
                            }}>
                              {displayRole}
                            </span>
                          </td>
                          <td style={{ padding: '14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            {user.project_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not Assigned</span>}
                          </td>
                          <td style={{ padding: '14px', whiteSpace: 'nowrap' }}>
                            {user.exit_status === 'EXIT_PENDING' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: '#fef3c7', color: '#b45309', display: 'inline-block', width: 'fit-content' }}>
                                  Exit Pending
                                </span>
                                {user.last_working_day && (
                                  <span style={{ fontSize: '11px', color: '#b45309' }}>LWD: {new Date(user.last_working_day).toLocaleDateString()}</span>
                                )}
                              </div>
                            ) : user.exit_status === 'EXIT_APPROVED' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: '#dcfce7', color: '#15803d', display: 'inline-block', width: 'fit-content' }}>
                                  Exit Approved
                                </span>
                                {user.last_working_day && (
                                  <span style={{ fontSize: '11px', color: '#15803d' }}>LWD: {new Date(user.last_working_day).toLocaleDateString()}</span>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#0d9488', fontWeight: '600', fontSize: '13px' }}>Active</span>
                            )}
                          </td>
                          <td style={{ padding: '14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                              <button 
                                onClick={() => openEditModal(user)}
                                style={{ background: 'transparent', border: 'none', color: '#4f46e5', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                              >
                                Edit
                              </button>
                              {user.id !== currentUserId && (
                                <button 
                                  onClick={() => handleDelete(user)}
                                  style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)} style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            background: 'var(--bg-card)', padding: '28px 32px', borderRadius: '20px', width: '100%', maxWidth: '520px', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '20px', color: 'var(--text-primary)' }}>
              {modalMode === 'add' ? 'Add User' : 'Edit User'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Full Name</label>
                <input 
                  type="text" 
                  value={formData.full_name}
                  onChange={e => setFormData({...formData, full_name: e.target.value})}
                  required
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Email</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={modalMode === 'edit'}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: modalMode === 'edit' ? 'var(--bg-page)' : 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Phone (Optional)</label>
                <input 
                  type="text" 
                  value={formData.phone_number}
                  onChange={e => setFormData({...formData, phone_number: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Role</label>
                <select 
                  value={formData.user_type}
                  onChange={e => setFormData({...formData, user_type: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                >
                  <option value="HR">HR Admin</option>
                  <option value="MANAGER">Project Manager</option>
                  <option value="BU_HEAD">Business Unit Head</option>
                </select>
              </div>

              {/* Status Column in Edit Modal */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Status</label>
                <select 
                  value={formData.exit_status}
                  onChange={e => setFormData({...formData, exit_status: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="EXIT_PENDING">Exit Pending (Notice Period)</option>
                  <option value="EXIT_APPROVED">Exit Approved / Offboarded</option>
                </select>
              </div>

              {/* Joined Date Field */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Joined Date</label>
                <input 
                  type="date" 
                  value={formData.joined_date}
                  onChange={e => setFormData({...formData, joined_date: e.target.value})}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                />
              </div>

              {/* Project Field (Editable if NOT assigned, Non-editable if ALREADY assigned) */}
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13.5px', color: 'var(--text-secondary)' }}>Project</label>
                {modalMode === 'edit' && isProjectAlreadyAssigned ? (
                  <div>
                    <input 
                      type="text" 
                      value={selectedUser.project_name} 
                      disabled 
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-page)', color: 'var(--text-primary)', fontSize: '14px', cursor: 'not-allowed' }} 
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                      🔒 Project is already assigned (Non-editable)
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select 
                      value={formData.use_custom_project ? 'CUSTOM' : formData.project_id}
                      onChange={e => {
                        if (e.target.value === 'CUSTOM') {
                          setFormData({...formData, use_custom_project: true, project_id: ''});
                        } else {
                          setFormData({...formData, use_custom_project: false, project_id: e.target.value, custom_project_name: ''});
                        }
                      }}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                    >
                      <option value="">-- Select Project --</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                      <option value="CUSTOM">+ Add Custom Project...</option>
                    </select>

                    {formData.use_custom_project && (
                      <input 
                        type="text" 
                        placeholder="Enter custom project name..." 
                        value={formData.custom_project_name}
                        onChange={e => setFormData({...formData, custom_project_name: e.target.value})}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: '14px' }}
                      />
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button type="button" disabled={submitting} onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: '30px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', opacity: submitting ? 0.6 : 1 }}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} style={{ padding: '10px 20px', borderRadius: '30px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: 'none', color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', opacity: submitting ? 0.8 : 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {submitting && <div className="schedule-docx-spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', borderTopColor: '#fff', animationDuration: '0.6s' }}></div>}
                  {modalMode === 'add' ? (submitting ? 'Adding...' : 'Add User') : (submitting ? 'Saving...' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementView;
